export type PaperUnit = "px" | "mm" | "inch" | "pt";

const DPI = 96;
const MM_PER_INCH = 25.4;
const PX_PER_MM = DPI / MM_PER_INCH;
const PT_PER_INCH = 72; // 1 inch = 72 points (standard in typography/printing)
const PX_PER_PT = DPI / PT_PER_INCH; // 96 / 72 = 1.333...

export const unitConversions = {
  mmToPx: (mm: number): number => mm * PX_PER_MM,
  inchToPx: (inch: number): number => inch * DPI,
  ptToPx: (pt: number): number => pt * PX_PER_PT,
  pxToMm: (px: number): number => Math.round((px / PX_PER_MM) * 100) / 100,
  pxToInch: (px: number): number => Math.round((px / DPI) * 1000) / 1000,
  pxToPt: (px: number): number => Math.round((px / PX_PER_PT) * 100) / 100,
  mmToInch: (mm: number): number => Math.round((mm / MM_PER_INCH) * 1000) / 1000,
  inchToMm: (inch: number): number => Math.round(inch * MM_PER_INCH * 100) / 100,
  ptToInch: (pt: number): number => Math.round((pt / PT_PER_INCH) * 1000) / 1000,
  inchToPt: (inch: number): number => Math.round(inch * PT_PER_INCH * 100) / 100,
  mmToPt: (mm: number): number => Math.round((mm / MM_PER_INCH) * PT_PER_INCH * 100) / 100,
  ptToMm: (pt: number): number => Math.round((pt / PT_PER_INCH) * MM_PER_INCH * 100) / 100
};

export const convertValue = (value: number, fromUnit: PaperUnit, toUnit: PaperUnit): number => {
  if (fromUnit === toUnit) return value;

  let pixelValue = value;
  if (fromUnit === "mm") {
    pixelValue = unitConversions.mmToPx(value);
  } else if (fromUnit === "inch") {
    pixelValue = unitConversions.inchToPx(value);
  } else if (fromUnit === "pt") {
    pixelValue = unitConversions.ptToPx(value);
  }

  if (toUnit === "px") return pixelValue;
  if (toUnit === "mm") return unitConversions.pxToMm(pixelValue);
  if (toUnit === "inch") return unitConversions.pxToInch(pixelValue);
  if (toUnit === "pt") return unitConversions.pxToPt(pixelValue);

  return value;
};

export const getMinMaxForUnit = (unit: PaperUnit): { min: number; max: number } => {
  switch (unit) {
    case "mm":
      return { min: 26, max: 676 }; // ~100px to ~2556px in mm
    case "inch":
      return { min: 1.04, max: 26.6 }; // ~100px to ~2556px in inches
    case "pt":
      return { min: 75, max: 1917 }; // ~100px to ~2556px in points (1pt = 1.333px)
    default:
      return { min: 100, max: 2556 };
  }
};

export const formatUnitLabel = (unit: PaperUnit): string => {
  switch (unit) {
    case "mm":
      return "mm";
    case "inch":
      return "in";
    case "pt":
      return "pt";
    default:
      return "px";
  }
};
